import React, { useState, useEffect, useRef, useCallback } from "react";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./FormsList.css";
import usePageTitle from "../usePageTitle";
import FormsSetsListHeaderComponent from "./FormsSetsListHeaderComponent";
import FormsSetsListDatatableComponent from "./FormsSetsListDatatableComponent";
import { FilterMatchMode } from "primereact/api";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";

import {
  fetchFormsSets,
  fetchUserFormsets,
  createFormSet,
  fetchForms,
  fetchUserCreatedForms,
  updateFormSetMembers,
  deleteFormSet,
} from "./api";

const FormsSetsList = () => {
  usePageTitle("Forms Sets List - SkeyeForm™");
  const [data, setData] = useState([]);
  const [allForms, setAllForms] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(24);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [userRole, setUserRole] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      const roles = userData?.roles?.map((r) => r.name) || [];

      const isAdmin = roles.includes("ROLE_ADMIN");
      const isSuperUser = roles.includes("ROLE_SUPERUSER");

      // Store user role for later use
      if (isAdmin) {
        setUserRole("ADMIN");
      } else if (isSuperUser) {
        setUserRole("SUPERUSER");
      }

      let formSets = [];
      let forms = [];

      if (isAdmin) {
        // ADMIN → fetch form sets + all forms
        [formSets, forms] = await Promise.all([fetchFormsSets(), fetchForms()]);

        // Ensure every form set exposes formIds (handles cases where API returns only `forms`)
        formSets = formSets.map((fs) => ({
          ...fs,
          formIds: Array.isArray(fs.formIds)
            ? fs.formIds
            : fs.forms?.map((form) => form.id) || [],
        }));
      } else if (isSuperUser) {
        // SUPERUSER → fetch user formsets + user created forms
        const [userFormSets, userForms] = await Promise.all([
          fetchUserFormsets(),
          fetchUserCreatedForms(),
        ]);
        formSets = userFormSets.map((fs) => ({
          ...fs,
          formIds: fs.forms?.map((f) => f.id) || [],
        }));
        forms = userForms;
      }

      if (Array.isArray(formSets)) {
        setData(formSets);
      } else {
        setData([]);
      }

      if (Array.isArray(forms)) {
        setAllForms(forms);
      } else {
        setAllForms([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (Array.isArray(data)) {
      let filtered = data.filter((formSet) => {
        return (
          formSet.name &&
          formSet.name.toLowerCase().includes(globalFilterValue.toLowerCase())
        );
      });

      if (sortField) {
        filtered = filtered.sort((a, b) => {
          const valueA = a[sortField];
          const valueB = b[sortField];
          let result = 0;

          if (valueA < valueB) result = -1;
          else if (valueA > valueB) result = 1;

          return sortOrder * result;
        });
      }
      setTotalRecords(filtered.length);
      setFilteredData(filtered.slice(first, first + rows));
    }
  }, [globalFilterValue, first, rows, data, sortField, sortOrder]);

  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    setFirst(0);
  };

  const clearFilter = () => {
    setGlobalFilterValue("");
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setFirst(0);
  };

  const onSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
  };

  const handleCreateFormSet = async (formSetData) => {
    try {
      await createFormSet(formSetData);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "formset created successfully.",
        life: 3000,
      });
      fetchData(); // Refresh data
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to create formset.",
        life: 3000,
      });
    }
  };

  const handleDeleteFormSet = async (formSetId) => {
    try {
      await deleteFormSet(formSetId);

      // Update local state immediately instead of refetching
      setData((prevData) => prevData.filter((fs) => fs.id !== formSetId));

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "formset deleted successfully.",
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete formset.",
        life: 3000,
      });
    }
  };

  const handleAddForms = async (formSetId, formIds) => {
    await updateFormSetMembers(formSetId, { add: formIds });
    // Update local state instead of refetching
    setData((prevData) =>
      prevData.map((fs) =>
        fs.id === formSetId
          ? { ...fs, formIds: [...(fs.formIds || []), ...formIds] }
          : fs
      )
    );
  };

  const handleRemoveForm = async (formSetId, formId) => {
    await updateFormSetMembers(formSetId, { remove: [formId] });
    // Update local state instead of refetching
    setData((prevData) =>
      prevData.map((fs) =>
        fs.id === formSetId
          ? { ...fs, formIds: fs.formIds.filter((id) => id !== formId) }
          : fs
      )
    );
  };

  const header = (
    <FormsSetsListHeaderComponent
      globalFilterValue={globalFilterValue}
      onGlobalFilterChange={onGlobalFilterChange}
      clearFilter={clearFilter}
      handleCreateFormSet={handleCreateFormSet}
    />
  );

  return (
    <div className="p-grid p-dir-col table-page">
      <Toast ref={toast} />
      <div className="p-col title">
        <h1>Formsets List</h1>
        <div className="title-bar"></div>
      </div>
      {loading ? (
        <div className="loader-container">
          <ProgressSpinner
            height={40}
            width={40}
            color="#D97A07"
            wrapperStyle={{ display: "inline-block", verticalAlign: "middle" }}
            wrapperClass=""
            visible={true}
            ariaLabel="oval-loading"
            secondaryColor="#D97A07"
            strokeWidth={2}
            strokeWidthSecondary={2}
          />
          <span
            style={{
              marginLeft: "10px",
              verticalAlign: "middle",
              fontSize: "20px",
              fontFamily: "Poppins",
            }}
          >
            Fetching Data (This Might Take a Minute)...
          </span>
        </div>
      ) : (
        <FormsSetsListDatatableComponent
          data={filteredData}
          allForms={allForms}
          userRole={userRole}
          onAddForms={handleAddForms}
          onRemoveForm={handleRemoveForm}
          onDeleteFormSet={handleDeleteFormSet}
          filters={filters}
          setFilters={setFilters}
          header={header}
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          emptyMessage="No Sets found"
          onSort={onSort}
          sortField={sortField}
          sortOrder={sortOrder}
        />
      )}
    </div>
  );
};

export default FormsSetsList;
